import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Grid, Card, CardContent, Button, Stack, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Alert, IconButton, LinearProgress, Table, TableHead, TableRow,
  TableCell, FormControlLabel, Checkbox, Rating,
} from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VerifiedIcon from '@mui/icons-material/Verified';
import { gql, useQuery as useGQLQuery, useMutation as useGQLMutation } from '@apollo/client';
import {
  ASTROLOGY_CONSULTANTS_QUERY,
  CREATE_ASTROLOGY_CONSULTANT_MUTATION, UPDATE_ASTROLOGY_CONSULTANT_MUTATION, DELETE_ASTROLOGY_CONSULTANT_MUTATION,
} from '../graphql/queries';
import ContractedVendorsSection from '../components/ContractedVendorsSection';

const SPEC_OPTIONS = ['muhurtham','horoscope_matching','kundali_milan','nadi_astrology','vastu','numerology','numerology','general'];
const LANGS = ['Tamil','Telugu','Kannada','Malayalam','Hindi','English','Sanskrit'];

const EMPTY = {
  name:'', title:'', city:'', state:'', contactPhone:'', email:'', website:'',
  specialisations:[], languagesSpoken:[], yearsExperience:'', pricePerSession:'',
  isOnline:true, isVerified:false, description:'', notes:'',
};

export default function AdminAstrologyPage() {
  const [error, setError]     = useState('');
  const [dialog, setDialog]   = useState(false);
  const [editC, setEditC]     = useState(null);
  const [form, setForm]       = useState(EMPTY);
  const [confirmDel, setConfirmDel] = useState(null);

  const { data, loading, refetch } = useQuery(ASTROLOGY_CONSULTANTS_QUERY, { fetchPolicy:'cache-and-network' });
  const consultants = data?.astrologyConsultants || [];

  const [createC] = useMutation(CREATE_ASTROLOGY_CONSULTANT_MUTATION, { onCompleted:()=>{ setDialog(false); setForm(EMPTY); setError(''); refetch(); }, onError:e=>setError(e.message) });
  const [updateC] = useMutation(UPDATE_ASTROLOGY_CONSULTANT_MUTATION, { onCompleted:()=>{ setDialog(false); setEditC(null); setError(''); refetch(); }, onError:e=>setError(e.message) });
  const [deleteC] = useMutation(DELETE_ASTROLOGY_CONSULTANT_MUTATION, { onCompleted:()=>{ setConfirmDel(null); refetch(); } });

  const openEdit = (c) => {
    setEditC(c);
    setForm({
      name:c.name||'', title:c.title||'', city:c.city||'', state:c.state||'',
      contactPhone:c.contactPhone||'', email:c.email||'', website:c.website||'',
      specialisations:c.specialisations||[], languagesSpoken:c.languagesSpoken||[],
      yearsExperience:c.yearsExperience||'', pricePerSession:c.pricePerSession||'',
      isOnline:c.isOnline!==false, isVerified:c.isVerified||false,
      description:c.description||'', notes:c.notes||'',
    });
    setDialog(true);
  };
  const handleSave = () => {
    const vars = { ...form, yearsExperience:form.yearsExperience?parseInt(form.yearsExperience):undefined, pricePerSession:form.pricePerSession?parseFloat(form.pricePerSession):undefined };
    editC ? updateC({ variables:{ id:editC.id, ...vars } }) : createC({ variables:vars });
  };
  const toggleSpec = (s) => setForm(f=>({ ...f, specialisations: f.specialisations.includes(s) ? f.specialisations.filter(x=>x!==s) : [...f.specialisations,s] }));
  const toggleLang = (l) => setForm(f=>({ ...f, languagesSpoken: f.languagesSpoken.includes(l) ? f.languagesSpoken.filter(x=>x!==l) : [...f.languagesSpoken,l] }));

  return (
    <Box sx={{ p:3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Admin — Astrology Consultants</Typography>
          <Typography variant="body2" color="text.secondary">Manage astrology & muhurtham consultants visible to users</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon/>} onClick={()=>{ setEditC(null); setForm(EMPTY); setDialog(true); }}>Add Consultant</Button>
      </Stack>

      <ContractedVendorsSection category="astrology" />
      {error && <Alert severity="error" sx={{mb:2}} onClose={()=>setError('')}>{error}</Alert>}
      {loading && <LinearProgress sx={{mb:2}}/>}

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell><TableCell>City</TableCell><TableCell>Specialisations</TableCell>
            <TableCell>Rating</TableCell><TableCell>Price/Session</TableCell><TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {consultants.map(c=>(
            <TableRow key={c.id}>
              <TableCell>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Box>
                    <Typography fontWeight={600}>{c.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{c.title}</Typography>
                  </Box>
                  {c.isVerified && <VerifiedIcon color="primary" sx={{fontSize:16}}/>}
                </Stack>
              </TableCell>
              <TableCell>{c.city||'—'}</TableCell>
              <TableCell>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                  {(c.specialisations||[]).map(s=><Chip key={s} size="small" label={s.replace('_',' ')} variant="outlined" color="primary"/>)}
                </Stack>
              </TableCell>
              <TableCell><Rating value={parseFloat(c.rating)||0} readOnly size="small" precision={0.5}/></TableCell>
              <TableCell>{c.pricePerSession!=null?`₹${Number(c.pricePerSession).toLocaleString('en-IN')}`:'—'}</TableCell>
              <TableCell>
                <IconButton size="small" onClick={()=>openEdit(c)}><EditIcon fontSize="small"/></IconButton>
                <IconButton size="small" color="error" onClick={()=>setConfirmDel(c.id)}><DeleteIcon fontSize="small"/></IconButton>
              </TableCell>
            </TableRow>
          ))}
          {consultants.length===0 && !loading && <TableRow><TableCell colSpan={6}><Typography textAlign="center" color="text.secondary" py={3}>No consultants yet.</Typography></TableCell></TableRow>}
        </TableBody>
      </Table>

      {/* Dialog */}
      <Dialog open={dialog} onClose={()=>setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editC?'Edit Consultant':'Add Consultant'}</DialogTitle>
        <DialogContent sx={{display:'flex',flexDirection:'column',gap:2,pt:2}}>
          <TextField label="Full Name *" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} fullWidth/>
          <TextField label="Title / Designation" value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} fullWidth placeholder="e.g. Vedic Astrologer, Muhurtham Specialist"/>
          <Grid container spacing={1.5}>
            <Grid item xs={6}><TextField label="City" value={form.city} onChange={e=>setForm(f=>({...f,city:e.target.value}))} fullWidth/></Grid>
            <Grid item xs={6}><TextField label="State" value={form.state} onChange={e=>setForm(f=>({...f,state:e.target.value}))} fullWidth/></Grid>
          </Grid>
          <Grid container spacing={1.5}>
            <Grid item xs={6}><TextField label="Phone" value={form.contactPhone} onChange={e=>setForm(f=>({...f,contactPhone:e.target.value}))} fullWidth/></Grid>
            <Grid item xs={6}><TextField label="Email" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} fullWidth/></Grid>
          </Grid>
          <Grid container spacing={1.5}>
            <Grid item xs={6}><TextField label="Experience (years)" type="number" value={form.yearsExperience} onChange={e=>setForm(f=>({...f,yearsExperience:e.target.value}))} fullWidth/></Grid>
            <Grid item xs={6}><TextField label="Price per Session (₹)" type="number" value={form.pricePerSession} onChange={e=>setForm(f=>({...f,pricePerSession:e.target.value}))} fullWidth/></Grid>
          </Grid>
          <TextField label="Website" value={form.website} onChange={e=>setForm(f=>({...f,website:e.target.value}))} fullWidth/>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{mb:0.5,display:'block'}}>Specialisations</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.5}>
              {SPEC_OPTIONS.filter((s,i,a)=>a.indexOf(s)===i).map(s=>(
                <Chip key={s} label={s.replace('_',' ')} onClick={()=>toggleSpec(s)}
                  color={form.specialisations.includes(s)?'primary':'default'}
                  variant={form.specialisations.includes(s)?'filled':'outlined'}/>
              ))}
            </Stack>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{mb:0.5,display:'block'}}>Languages</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.5}>
              {LANGS.map(l=>(
                <Chip key={l} label={l} onClick={()=>toggleLang(l)}
                  color={form.languagesSpoken.includes(l)?'primary':'default'}
                  variant={form.languagesSpoken.includes(l)?'filled':'outlined'}/>
              ))}
            </Stack>
          </Box>
          <Stack direction="row" spacing={2}>
            <FormControlLabel control={<Checkbox checked={!!form.isOnline} onChange={e=>setForm(f=>({...f,isOnline:e.target.checked}))}/>} label="Available Online"/>
            <FormControlLabel control={<Checkbox checked={!!form.isVerified} onChange={e=>setForm(f=>({...f,isVerified:e.target.checked}))}/>} label="Verified"/>
          </Stack>
          <TextField label="Description" value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} multiline rows={3} fullWidth/>
          <TextField label="Notes" value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} multiline rows={2} fullWidth/>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.name}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete */}
      <Dialog open={!!confirmDel} onClose={()=>setConfirmDel(null)}>
        <DialogTitle>Delete Consultant?</DialogTitle>
        <DialogContent><Typography>This will permanently remove the consultant profile.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={()=>setConfirmDel(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={()=>deleteC({variables:{id:confirmDel}})}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
